import { Scale, FileSearch, Target } from "lucide-react";

export const Subrogation = () => {
  return (
    <section className="relative py-20 sm:py-24 md:py-36 overflow-hidden btr-bg-graphite" data-testid="subrogation-section">
      <div className="btr-beam btr-beam-drift" style={{ width: 600, height: 600, top: "30%", left: "-20%", background: "#a2020b", opacity: 0.2 }} />

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Visual */}
        <div className="btr-reveal relative order-2 lg:order-1">
          <div className="relative aspect-[4/5] max-w-md mx-auto lg:mx-0">
            <div className="absolute inset-0 btr-glass rounded-xl overflow-hidden">
              {/* Document body */}
              <div className="btr-doc absolute inset-0 p-8 bg-[linear-gradient(180deg,#0a0a0a_0%,#050505_100%)]">
                <div className="flex items-center justify-between pb-4 border-b border-white/10">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-display">
                      Subrogation File
                    </div>
                    <div className="font-display font-bold text-white text-sm mt-1">Case No. BTR-2024-0184</div>
                  </div>
                  <div className="text-[10px] font-display text-[#a2020b] tracking-[0.2em]">ACTIVE</div>
                </div>

                <div className="mt-6 space-y-3">
                  {[
                    "Originating carrier verification",
                    "Liability causation analysis",
                    "Third-party contribution map",
                    "Evidence preservation log",
                    "Legal-ready narrative",
                  ].map((row, i) => (
                    <div key={row} className="flex items-center justify-between text-xs font-body text-zinc-400">
                      <span>{row}</span>
                      <span className={i < 3 ? "text-[#a2020b]" : "text-zinc-600"}>
                        {i < 3 ? "VERIFIED" : "PENDING"}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Redacted blocks */}
                <div className="mt-6 space-y-2">
                  {[100, 88, 74, 92, 62, 80].map((w, i) => (
                    <div key={i} className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-white/15" style={{ width: `${w}%` }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Evidence markers */}
              <div className="absolute top-1/3 right-6 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#a2020b] btr-red-glow" />
                <span className="text-[9px] font-display tracking-[0.25em] uppercase text-zinc-400">Evidence Marker</span>
              </div>

              {/* Scanning line */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="btr-scan-line" />
              </div>
            </div>

            {/* Corner accent */}
            <div className="absolute -top-3 -right-3 w-16 h-16 border-t-2 border-r-2 border-[#a2020b]" />
            <div className="absolute -bottom-3 -left-3 w-16 h-16 border-b-2 border-l-2 border-[#a2020b]" />
          </div>
        </div>

        {/* Copy */}
        <div className="btr-reveal order-1 lg:order-2">
          <span className="btr-badge">
            <span className="dot" />
            Subrogation & Liability
          </span>
          <h2 className="font-display font-extrabold text-white text-3xl sm:text-5xl lg:text-[3.5rem] mt-6 leading-[1.05] sm:leading-[1.02] tracking-tight">
            Where Risk Management meets <span className="text-[#a2020b]">Legal Strategy</span>.
          </h2>
          <p className="font-body text-zinc-400 text-base sm:text-lg mt-6 leading-relaxed">
            With deep experience in subrogation, Before The Risk understands not only how losses are paid,
            but how liability is traced, assigned, documented, and challenged. Every report is built with
            legal pressure in mind.
          </p>

          <div className="mt-9 space-y-4">
            {[
              { icon: FileSearch, t: "Causation Trace", d: "Document the origin and sequence of loss events." },
              { icon: Target, t: "Liability Mapping", d: "Identify responsible parties with defensible clarity." },
              { icon: Scale, t: "Court-Ready Format", d: "Structured for courtroom, mediation, or arbitration." },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.t} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-md bg-[#a2020b]/10 border border-[#a2020b]/25 flex items-center justify-center flex-shrink-0 group-hover:bg-[#a2020b]/20 transition-colors">
                    <Icon className="w-4.5 h-4.5 text-[#a2020b]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-white text-sm tracking-wide">{p.t}</div>
                    <div className="font-body text-zinc-500 text-sm mt-1">{p.d}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
